import {
  Button,
  Checkbox,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  makeStyles,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { colors } from '../../../themes';

type Props = {
  loading: boolean;
  options?: string[];
  onConfirm: (descriptions: string[]) => void;
  onCancel: () => void;
};

const DEFAULT_ISSUE_OPTIONS = [
  'Missing Pages',
  'Blurry',
  'Not completed in AS400',
  'Other',
];

const BillingIssueModal: React.FC<Props> = props => {
  const {
    loading,
    options = DEFAULT_ISSUE_OPTIONS,
    onConfirm,
    onCancel,
  } = props;
  const classes = useStyles();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([
    options[0],
  ]);
  const [otherText, setOtherText] = useState('');
  const [open, setOpen] = useState(true);
  const [hasSelectionError, setHasSelectionError] = useState(false);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = event.target;
      setSelectedReasons(prev => {
        const next = checked
          ? [...prev, value]
          : prev.filter(item => item !== value);
        if (next.length > 0) {
          setHasSelectionError(false);
        }
        return next;
      });
    },
    [],
  );

  const handleOtherTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOtherText(event.target.value);
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (selectedReasons.length === 0) {
      setHasSelectionError(true);
      return;
    }

    const finalDescriptions = selectedReasons.map(reason =>
      reason === 'Other' ? otherText.trim() || 'Other' : reason,
    );

    onConfirm(finalDescriptions);
  }, [selectedReasons, otherText, onConfirm]);

  return (
    <Dialog
      aria-labelledby="driver-confirm-dialog-title"
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          setOpen(false);
        }
      }}
      fullWidth
      maxWidth="xs"
      className={classes.dialog}
    >
      <DialogTitle disableTypography className={classes.header}>
        <span className="linked-text">Billing Issue</span>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.main}>
        <div className="driver-text">
          Please select all applicable categories for the issue
        </div>

        <ValidatorForm className={classes.form} onSubmit={handleConfirm}>
          <FormControl
            component="fieldset"
            error={hasSelectionError}
            className={classes.radioContainer}
          >
            <FormGroup>
              {options.map(option => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={selectedReasons.includes(option)}
                      onChange={handleCheckboxChange}
                      value={option}
                      color="primary"
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            {hasSelectionError && (
              <FormHelperText>Please select at least one issue</FormHelperText>
            )}
          </FormControl>

          {selectedReasons.includes('Other') && (
            <TextValidator
              multiLine
              rows={3}
              name="otherDescription"
              className={classes.input}
              disabled={loading}
              label="Please specify issue"
              onChange={handleOtherTextChange}
              value={otherText}
              variant="outlined"
              validators={['required']}
              errorMessages={['This field is required']}
            />
          )}

          <div className={classes.actions}>
            <Button
              type="button"
              disabled={loading}
              className={`${classes.actionBtn} ${classes.cancelBtn}`}
              onClick={onCancel}
            >
              Discard
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className={`${classes.actionBtn} ${classes.saveBtn}`}
            >
              Confirm Issue
            </Button>
          </div>
        </ValidatorForm>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paperWidthXs': {
      maxWidth: 500,
    },
  },
  header: {
    fontSize: 18,
    color: '#4f4f4f',
    fontWeight: 700,
    textAlign: 'center',
    padding: theme.spacing(1),
    '& .linked-text': {
      textTransform: 'uppercase',
    },
  },
  main: {
    padding: theme.spacing(1, 0, 0),
    alignItems: 'center',
    '& .driver-text': {
      textAlign: 'center',
      fontSize: 16,
      padding: theme.spacing(0.5, 3),
      color: '#4f4f4f',
      fontWeight: 500,
      marginBottom: theme.spacing(1),
    },
  },
  radioContainer: {
    padding: theme.spacing(0, 4),
    width: '100%',
  },
  actions: {
    alignItems: 'center',
    borderTop: '1px solid #bdbdbd',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2),
    width: '100%',
  },
  actionBtn: {
    borderRadius: 5,
    fontSize: theme.typography.body1.fontSize,
    marginLeft: theme.spacing(2),
    padding: theme.spacing(1, 4),
  },
  cancelBtn: {
    backgroundColor: colors.danger[500],
    color: 'white',
    '&:hover': {
      backgroundColor: darken(colors.danger[500], 0.15),
    },
  },
  saveBtn: {
    backgroundColor: colors.textPrimary[400],
    color: 'white',
    '&:hover': {
      backgroundColor: darken(colors.textPrimary[400], 0.15),
    },
  },
  input: {
    margin: theme.spacing(1, 4, 0),
    width: 'calc(100% - 64px)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
}));

export default observer(BillingIssueModal);
